import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ClientLink,
  DateTime,
  ExportDropdown,
  Hours,
  Icon,
  InternalClientTooltip,
  PeriodFilter,
  ProjectLink,
  Tooltip,
  YesNoFilter,
} from '~/components';
import AllocationBillableTypeFilter from '~/components/filters/AllocationBillableTypeFilter';
import ClientFiltersBar from '~/components/filters/ClientFiltersBar';
import ClientFiltersGroup from '~/components/filters/ClientFiltersGroup';
import FilterButton from '~/components/filters/FilterButton';
import FiltersGroup from '~/components/filters/FiltersGroup';
import MemberFiltersBar from '~/components/filters/MemberFiltersBar';
import MemberFiltersGroup from '~/components/filters/MemberFiltersGroup';
import ProjectFiltersBar from '~/components/filters/ProjectFiltersBar';
import ProjectFiltersGroup from '~/components/filters/ProjectFiltersGroup';
import ResourceFiltersBar from '~/components/filters/ResourceFiltersBar';
import ResourceFiltersGroup from '~/components/filters/ResourceFiltersGroup';
import SingleSelectFilter from '~/components/filters/SingleSelectFilter';
import AllocationBookingTypeFilter from '~/components/filters/AllocationBookingTypeFilter';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useResourceFilters, useSearchParams, useSearchParamsConfig } from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useMemberFilters from '~/hooks/useMemberFilters';
import useProjectFilters from '~/hooks/useProjectFilters';
import allocationBillableTypes from '~/lookups/allocation-billable-types';
import projectStatuses from '~/lookups/project-statuses.js';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, mimeTypes } from '~/utils';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import ReportActions from '../ReportActions';
import { intervals, intervalsByScope } from './intervals';

const ProjectInfo = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const Member = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
`;

export default function HoursPlanByProjectAndResource({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const searchParamsConfig = useSearchParamsConfig();

  const [query, setQuery] = useState({ report: null, status: 'loading' });
  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters(() => ({
    projectRecordStatusId: {
      ...searchParamsConfig.projectRecordStatusId,
      default: 'active',
    },
    projectStatuses: {
      ...searchParamsConfig.projectStatuses,
      default: [projectStatuses.not_started, projectStatuses.in_progress],
    },
  }));
  const memberFilters = useMemberFilters();
  const resourceFilters = useResourceFilters();

  const [params, setParams] = useState({
    period: null,
    unit: 'month',

    includeProjectMembers: false,

    // Allocation Filters
    allocationBillableTypes: [allocationBillableTypes.billable],
    allocationBookingTypeId: null,

    ...memberFilters.filters,
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...resourceFilters.filters,
  });

  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.periodByUnit,
          default: intervals.next_6_months,
          intervals: (unit) => intervalsByScope[unit],
          defaults: {
            day: intervals.next_30_days,
            week: intervals.next_12_weeks,
            month: intervals.next_6_months,
          },
        },
        unit: { default: 'month', valid: ['day', 'week', 'month'] },

        includeProjectMembers: {
          default: false,
          serialize: (value) => (value === true ? 'true' : 'false'),
          deserialize: (value) => value === 'true',
        },

        allocationBillableTypes: {
          ...searchParamsConfig.allocationBillableTypes,
          default: [allocationBillableTypes.billable],
        },
        allocationBookingTypeId: searchParamsConfig.allocationBookingTypeId,

        ...memberFilters.searchParamsConfig,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...resourceFilters.searchParamsConfig,
      }),
      [searchParamsConfig, clientFilters, projectFilters, memberFilters, resourceFilters],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      unit: params.unit,

      includeProjectMembers: params.includeProjectMembers === true ? 'true' : undefined,

      // Allocation Filters
      allocationBillableTypeId: params.allocationBillableTypes?.map((v) => v.id),
      allocationBookingTypeId: params.allocationBookingTypeId || undefined,

      ...memberFilters.mapUrlSearchParams(params),
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...resourceFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters, resourceFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideFilters();
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />

          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <PeriodFilter
          clearable={false}
          scope={params.unit}
          intervals={intervalsByScope[params.unit]}
          placeholder="Date Range"
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />

        <UnitFilter
          value={params.unit}
          onChange={({ target: { value } }) =>
            handleApplyFilters({
              unit: value,
              period: {
                day: intervals.next_30_days,
                week: intervals.next_12_weeks,
                month: intervals.next_6_months,
              }[value],
            })
          }
        />

        <YesNoFilter
          icon="filter"
          placeholder="Members without allocations"
          value={params.includeProjectMembers ? 'yes' : 'no'}
          onChange={({ target: { value } }) => handleApplyFilters({ includeProjectMembers: value === 'yes' })}
        />

        <AllocationBookingTypeFilter
          value={params.allocationBookingTypeId}
          onChange={({ target: { value } }) => handleApplyFilters({ allocationBookingTypeId: value })}
        />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />

        <ResourceFiltersBar filters={params} onChange={handleApplyFilters} />

        {!_.isEmpty(params.allocationBillableTypes) && (
          <AllocationBillableTypeFilter
            value={params.allocationBillableTypes}
            onChange={({ target: { value } }) => handleApplyFilters({ allocationBillableTypes: value })}
          />
        )}
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params }) {
  const report = query.data;

  const { projects, resourcesById, allocationGroups, periods } = useMemo(() => {
    const projects = _.orderBy(report.projects, (p) => p.name.toLowerCase());

    const resourcesById = _.keyBy(report.resources, 'id');

    const allocationGroups = _.orderBy(report.allocations, (a) =>
      resourcesById[a.resourceId].name.toLowerCase(),
    ).reduce(
      (a, v) => {
        a.total += v.totalHours;

        // Projects
        a.projects[v.projectId] = a.projects[v.projectId] || { total: 0, periods: {}, resources: {} };
        a.projects[v.projectId].total += v.totalHours;
        if (!a.projects[v.projectId].periods[v.start]) a.projects[v.projectId].periods[v.start] = 0;
        a.projects[v.projectId].periods[v.start] += v.totalHours;
        a.projects[v.projectId].resources[v.resourceId] = a.projects[v.projectId].resources[v.resourceId] || {
          total: 0,
          periods: {},
        };

        if (v.start) {
          // Resources
          a.projects[v.projectId].resources[v.resourceId].total += v.totalHours;
          a.projects[v.projectId].resources[v.resourceId].periods[v.start] = v.totalHours;

          // Periods
          if (!a.periods[v.start]) a.periods[v.start] = 0;
          a.periods[v.start] += v.totalHours;
        }

        return a;
      },
      { projects: {}, periods: {}, total: 0 },
    );

    let start = params.period.start;
    let end = params.period.end;

    switch (params.unit) {
      case 'day':
        start = moment(start).format(dateFormats.isoDate);
        end = moment(end).format(dateFormats.isoDate);
        break;

      case 'week':
        start = moment(start).startOf('isoWeek').format(dateFormats.isoDate);
        end = moment(end).endOf('isoWeek').format(dateFormats.isoDate);
        break;

      case 'month':
        start = moment(start).startOf('month').format(dateFormats.isoDate);
        end = moment(end).endOf('month').format(dateFormats.isoDate);
        break;
    }

    const periodCount = moment(end).diff(start, params.unit) + 1;
    const periods = [];
    for (let index = 0; index < periodCount; index++) {
      periods.push(moment(start).add(index, params.unit).format(dateFormats.isoDate));
    }

    return { projects, resourcesById, allocationGroups, periods };
  }, [report, params.period.start, params.period.end, params.unit]);

  return (
    <Report.Table>
      <Table>
        <Table.Header>
          <Table.Column sticky minWidth="16rem">
            Project/Client
          </Table.Column>

          {periods.map((p) => (
            <Table.Column key={p} align="right" width="7rem">
              <DateTime value={p} />
            </Table.Column>
          ))}

          <Table.Column align="right" width="7rem">
            Total
          </Table.Column>
        </Table.Header>

        <Table.Body>
          {projects.map((project) => {
            return (
              <React.Fragment key={project.id}>
                <Table.GroupRow top="3rem">
                  <Table.Cell>
                    <ProjectInfo>
                      <p>
                        <ProjectLink project={project} />
                      </p>
                      <small>
                        <ClientLink client={project.client} />
                        {project.client.isInternal && <InternalClientTooltip />}
                      </small>
                    </ProjectInfo>
                  </Table.Cell>

                  {periods.map((period) => {
                    const hours = allocationGroups.projects[project.id].periods[period];

                    return (
                      <Table.Cell key={period}>
                        <strong>
                          <Hours value={hours} />
                        </strong>
                      </Table.Cell>
                    );
                  })}

                  <Table.Cell>
                    <strong>
                      <Hours value={allocationGroups.projects[project.id].total} />
                    </strong>
                  </Table.Cell>
                </Table.GroupRow>

                {_.map(allocationGroups.projects[project.id].resources, (resourceAllocations, key) => {
                  const resource = resourcesById[key];

                  return (
                    <Table.Row key={key}>
                      <Table.Cell>
                        <Member>
                          {resource.name}
                          {{
                            member: () => (
                              <Tooltip message="Member">
                                <Icon icon="user" color={colors.grey25} spaceLeft />
                              </Tooltip>
                            ),
                            placeholder: () => (
                              <Tooltip message="Resource Placeholder">
                                <Icon icon="users" color={colors.grey25} spaceLeft />
                              </Tooltip>
                            ),
                          }[resource.resourceType]()}
                        </Member>
                      </Table.Cell>

                      {periods.map((period) => {
                        const hours = resourceAllocations.periods[period];

                        return (
                          <Table.Cell key={period}>
                            <Hours value={hours} />
                          </Table.Cell>
                        );
                      })}

                      <Table.Cell>
                        <Hours value={resourceAllocations.total} />
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </React.Fragment>
            );
          })}

          <Table.Row style={{ fontWeight: weights.bold }}>
            <Table.Cell>Total</Table.Cell>

            {periods.map((period) => {
              const hours = allocationGroups.periods[period];

              return (
                <Table.Cell key={period}>
                  <Hours value={hours} />
                </Table.Cell>
              );
            })}

            <Table.Cell>
              <Hours value={allocationGroups.total} />
            </Table.Cell>
          </Table.Row>
        </Table.Body>

        <Table.Status total={projects.length} label="Project" isLoading={query.status !== 'ready'} />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <UnitFilter
        value={filters.unit}
        onChange={({ target: { value } }) =>
          handleFilter({
            unit: value,
            period: {
              day: intervals.next_30_days,
              week: intervals.next_12_weeks,
              month: intervals.next_6_months,
            }[value],
          })
        }
      />

      <PeriodFilter
        clearable={false}
        scope={filters.unit}
        intervals={intervalsByScope[filters.unit]}
        placeholder="Date Range"
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <YesNoFilter
        icon="filter"
        placeholder="Include members without allocations"
        value={filters.includeProjectMembers ? 'yes' : 'no'}
        onChange={({ target: { value } }) => handleFilter({ includeProjectMembers: value === 'yes' })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <ResourceFiltersGroup filters={filters} onChange={handleFilter} />

      <FiltersGroup
        label="Allocation Filters"
        filters={[filters.allocationBookingTypeId, filters.allocationBillableTypes]}>
        <AllocationBookingTypeFilter
          value={filters.allocationBookingTypeId}
          onChange={({ target: { value } }) => handleFilter({ allocationBookingTypeId: value })}
        />

        <AllocationBillableTypeFilter
          value={filters.allocationBillableTypes}
          onChange={({ target: { value } }) => handleFilter({ allocationBillableTypes: value })}
        />
      </FiltersGroup>
    </Report.FiltersDrawer>
  );
}

function UnitFilter(props) {
  return (
    <SingleSelectFilter
      icon="gear"
      placeholder="Unit"
      clearable={false}
      renderValue={(value) => value.name}
      options={[
        { id: 'month', name: 'Month' },
        { id: 'week', name: 'Week' },
        { id: 'day', name: 'Day' },
      ]}
      {...props}
    />
  );
}
